import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableConfigDialog } from "../tables/TableConfigDialog";
import { teamMembersFiltersAreEqual, type TeamMembersFilters } from "./teamMembersFilters";
import { TeamMembersFilterConfigForm } from "./TeamMembersFilterConfigForm";
import { ColumnConfigForm, type ColumnConfigFormProps } from "../tables/ColumnConfigForm";
import type { UseTableViewConfigResult } from "../../hooks/useTableViewConfig";

export interface TeamMembersTableConfigDialogProps
  extends Pick<UseTableViewConfigResult<TeamMembersFilters>, "onApplyConfig" | "activeFilters"> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClearAll?: () => void;
  title?: string;
  description?: string;
  columnConfigFormProps: Pick<
    ColumnConfigFormProps,
    "columnVisibility" | "orderedVisibleColumnIds" | "combinedFieldToColumnConfigMap" | "title"
  >;
  isDirty?: boolean;
  tagOptions: string[];
  roleOptions: string[];
}

export const TeamMembersTableConfigDialog = ({
  onApplyConfig,
  activeFilters,
  onClearAll,
  title,
  description,
  columnConfigFormProps: {
    columnVisibility,
    orderedVisibleColumnIds,
    combinedFieldToColumnConfigMap,
    title: columnConfigTitle,
  },
  isDirty: providedIsDirty,
  tagOptions,
  roleOptions,
  ...rest
}: TeamMembersTableConfigDialogProps) => {
  const { t } = useTranslation("settings");

  const [stagedColumnIds, setStagedColumnIds] = useState<string[]>(orderedVisibleColumnIds);
  const [stagedFilters, setStagedFilters] = useState<TeamMembersFilters>(activeFilters);

  const isColumnsDirty = useMemo(() => {
    if (stagedColumnIds.length !== orderedVisibleColumnIds.length) return true;
    return stagedColumnIds.some((id, i) => id !== orderedVisibleColumnIds[i]);
  }, [stagedColumnIds, orderedVisibleColumnIds]);

  const isFiltersDirty = useMemo(
    () => !teamMembersFiltersAreEqual(stagedFilters, activeFilters),
    [stagedFilters, activeFilters]
  );

  const isDirty = providedIsDirty || isColumnsDirty || isFiltersDirty;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setStagedColumnIds(orderedVisibleColumnIds);
      setStagedFilters(activeFilters);
    }
    rest.onOpenChange?.(open);
  };

  const handleApply = () => {
    onApplyConfig(stagedColumnIds, stagedFilters);
  };

  return (
    <TableConfigDialog
      {...rest}
      onOpenChange={handleOpenChange}
      onClearAll={onClearAll}
      title={title ?? t("Configure Team Table View")}
      description={description ?? t("Add and remove columns, and apply filters to narrow down team members")}
      isDirty={isDirty}
      onApply={handleApply}
    >
      <ColumnConfigForm
        columnVisibility={columnVisibility}
        orderedVisibleColumnIds={orderedVisibleColumnIds}
        combinedFieldToColumnConfigMap={combinedFieldToColumnConfigMap}
        stagedColumnIds={stagedColumnIds}
        onStagedColumnIdsChange={setStagedColumnIds}
        title={columnConfigTitle}
        isDirty={isColumnsDirty}
        reset={() => setStagedColumnIds(orderedVisibleColumnIds)}
      />
      <TeamMembersFilterConfigForm
        stagedFilters={stagedFilters}
        onStagedFiltersChange={setStagedFilters}
        tagOptions={tagOptions}
        roleOptions={roleOptions}
      />
    </TableConfigDialog>
  );
};
